'use client';

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clipboard, Copy, RotateCcw, Square } from 'lucide-react';
import { formatDayLabel } from './scheduleBlockUtils';
import type { TimeSlot, TimeSlotsByDate } from './scheduleBlockUtils';
import { TimeSlotChip } from './TimeSlotChip';
import { cn } from '@/lib/utils';
import { isKnockoutSlot, knockoutDayLabel, type KnockoutStart } from '@/utils/tournament-schedule';

export interface DayScheduleCardProps {
  dateStr: string;
  slots: TimeSlot[];
  timeSlotsByDate: TimeSlotsByDate;
  maxCourts: number;
  onSlotChange: (dateStr: string, startTime: string, courtCount: number) => void;
  /** startTimes marked with check (sync with each other). */
  checkedSlotsForDay?: string[];
  /** When active, checkboxes are shown and range selection (drag) is allowed. */
  massSelectionEnabled?: boolean;
  onEnableMassSelection?: () => void;
  /** Should disable mass mode and clear checks for the day. */
  onDisableMassSelection?: () => void;
  onToggleCheck?: (dateStr: string, startTime: string) => void;
  /** Mark a range of time slots (drag). */
  onCheckRange?: (dateStr: string, startTimes: string[]) => void;
  onCopy: (dateStr: string) => void;
  onResetDay: (dateStr: string) => void;
  onPaste?: (dateStr: string) => void;
  hasCopiedSlots: boolean;
  /** Index for staggered animation */
  index?: number;
  className?: string;
  /** SHORT: slot from which the bracket is played. */
  knockoutStart?: KnockoutStart | null;
  /** SHORT without a cut: the day the whole bracket is played on. */
  finalDay?: string | null;
  /** Present only when the knockout cut can be set from the grid. */
  onToggleKnockoutStart?: (dateStr: string, startTime: string) => void;
}

export function DayScheduleCard({
  dateStr,
  slots,
  timeSlotsByDate,
  maxCourts,
  onSlotChange,
  checkedSlotsForDay = [],
  massSelectionEnabled = false,
  onEnableMassSelection,
  onDisableMassSelection,
  onToggleCheck,
  onCheckRange,
  onCopy,
  onResetDay,
  onPaste,
  hasCopiedSlots,
  index = 0,
  className,
  knockoutStart = null,
  finalDay = null,
  onToggleKnockoutStart,
}: DayScheduleCardProps) {
  const byTime = timeSlotsByDate[dateStr] ?? {};
  const hasSlots = Object.values(byTime).some((c) => c > 0);

  const knockoutBadge = knockoutDayLabel(
    dateStr,
    slots.map((s) => s.startTime).filter((startTime) => (byTime[startTime] ?? 0) > 0),
    finalDay,
    knockoutStart,
  );

  const [dragAnchor, setDragAnchor] = useState<string | null>(null);
  const [dragCurrent, setDragCurrent] = useState<string | null>(null);

  const orderedStartTimes = slots.map((s) => s.startTime);

  const getRange = useCallback(
    (anchor: string | null, current: string | null): string[] => {
      if (!anchor) return [];
      const i = orderedStartTimes.indexOf(anchor);
      if (i === -1) return [anchor];
      if (!current) return [anchor];
      const j = orderedStartTimes.indexOf(current);
      if (j === -1) return [anchor];
      const lo = Math.min(i, j);
      const hi = Math.max(i, j);
      return orderedStartTimes.slice(lo, hi + 1);
    },
    [orderedStartTimes],
  );

  const dragRange = getRange(dragAnchor, dragCurrent);
  const isDragging = dragAnchor !== null;

  const handleSlotMouseDown = useCallback(
    (startTime: string) => {
      if (!onCheckRange) return;
      setDragAnchor(startTime);
      setDragCurrent(startTime);
    },
    [onCheckRange],
  );

  const handleSlotMouseEnter = useCallback((startTime: string) => {
    setDragCurrent((prev) => (dragAnchor !== null ? startTime : prev));
  }, [dragAnchor]);

  const handleMouseUp = useCallback(() => {
    if (dragAnchor === null) return;
    if (onCheckRange) {
      const range = getRange(dragAnchor, dragCurrent);
      if (range.length > 1) {
        onCheckRange(dateStr, range);
      }
      // Single slot: no toggle; selection is exclusive to the checkbox.
    }
    setDragAnchor(null);
    setDragCurrent(null);
  }, [dateStr, dragAnchor, dragCurrent, getRange, onCheckRange]);

  useEffect(() => {
    if (!isDragging) return;
    const onUp = () => handleMouseUp();
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, [isDragging, handleMouseUp]);

  return (
    <Card
      className={cn(
        'overflow-hidden transition-all',
        'animate-in fade-in slide-in-from-bottom-2 duration-300',
        className,
      )}
      style={{ animationDelay: `${index * 40}ms`, animationFillMode: 'backwards' }}
    >
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold capitalize">
            {formatDayLabel(dateStr)}
          </h3>
          {knockoutBadge && (
            <Badge
              variant="outline"
              className="border-amber-500 bg-amber-50 text-amber-800 hover:text-amber-800"
            >
              {knockoutBadge}
            </Badge>
          )}
        </div>
        <div className="flex shrink-0 gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 px-0 sm:w-auto sm:px-3"
            onClick={() => onCopy(dateStr)}
            disabled={!hasSlots}
            title="Copiar horarios"
          >
            <Copy className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Copiar</span>
          </Button>
          {onPaste && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 px-0 sm:w-auto sm:px-3"
              onClick={() => onPaste(dateStr)}
              disabled={!hasCopiedSlots}
              title="Pegar horarios"
            >
              <Clipboard className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Pegar</span>
            </Button>
          )}
          {!massSelectionEnabled && onEnableMassSelection && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 px-0 sm:w-auto sm:px-3"
              onClick={onEnableMassSelection}
              title="Activar selección masiva"
            >
              <Square className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Selección masiva</span>
            </Button>
          )}
          {massSelectionEnabled && onDisableMassSelection && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 px-0 sm:w-auto sm:px-3"
              onClick={onDisableMassSelection}
              title="Desactivar selección masiva"
            >
              <Square className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Desactivar selección masiva</span>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 px-0 text-destructive hover:bg-destructive/10 hover:text-destructive sm:w-auto sm:px-3"
            onClick={() => onResetDay(dateStr)}
            disabled={!hasSlots}
            title="Resetear día"
          >
            <RotateCcw className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Reset</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {slots.map((slot) => {
            const courtCount = byTime[slot.startTime] ?? 0;
            const hasCourts = courtCount > 0;
            const isCut =
              knockoutStart?.date === dateStr && knockoutStart.startTime === slot.startTime;
            const knockoutRole =
              hasCourts && isKnockoutSlot(dateStr, slot.startTime, finalDay, knockoutStart)
                ? (isCut ? 'start' : 'after')
                : undefined;
            return (
              <TimeSlotChip
                key={slot.startTime}
                slot={slot}
                courtCount={courtCount}
                maxCourts={maxCourts}
                onCountChange={(next) => onSlotChange(dateStr, slot.startTime, next)}
                isChecked={checkedSlotsForDay.includes(slot.startTime)}
                onToggleCheck={onToggleCheck ? () => onToggleCheck(dateStr, slot.startTime) : undefined}
                onMouseDown={onCheckRange ? () => handleSlotMouseDown(slot.startTime) : undefined}
                onMouseEnter={onCheckRange ? () => handleSlotMouseEnter(slot.startTime) : undefined}
                isDragPreview={dragRange.includes(slot.startTime)}
                knockoutRole={knockoutRole}
                onToggleKnockoutStart={
                  onToggleKnockoutStart && hasCourts
                    ? () => onToggleKnockoutStart(dateStr, slot.startTime)
                    : undefined
                }
              />
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
